/**
 * DebugDashboard Component
 *
 * Dashboard for viewing pipeline debug traces and WebSocket connection traces.
 * Shows trace history, timing, status, and detailed step information.
 * Two-level tracing: Connection (WebSocket lifecycle) -> Pipeline (query processing)
 * Admin users can see all traces; regular users see only their own.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Activity, Clock, CheckCircle, XCircle, AlertTriangle, HelpCircle,
  RefreshCw, ChevronDown, ChevronRight, Eye, Loader, Database,
  Zap, Code, BarChart2, X, Wifi, WifiOff, MessageSquare, Link2, ArrowRight
} from 'lucide-react';
import { useSettings } from '../hooks/useSettings';
import api from '../services/api';

const Container = styled.div`
  flex: 1;
  padding: 32px;
  overflow-y: auto;
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const Header = styled.div`
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    width: 28px;
    height: 28px;
    color: #1A8B8B;
  }
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #718096;
  margin: 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 32px;
`;

const StatCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;

  .stat-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
  }

  .stat-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${props => props.$color || '#1A8B8B'}20;
    color: ${props => props.$color || '#1A8B8B'};

    svg {
      width: 20px;
      height: 20px;
    }
  }

  .stat-label {
    font-size: 13px;
    color: #718096;
    font-weight: 500;
  }

  .stat-value {
    font-size: 28px;
    font-weight: 700;
    color: #2d3748;
  }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  align-items: center;
`;

const FilterSelect = styled.div`
  position: relative;

  select {
    appearance: none;
    padding: 10px 36px 10px 14px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 14px;
    color: #4a5568;
    background: white;
    cursor: pointer;
    min-width: 140px;

    &:focus {
      outline: none;
      border-color: #1A8B8B;
    }
  }

  svg {
    position: absolute;
    top: 50%;
    ${props => props.$isRTL ? 'left: 12px;' : 'right: 12px;'}
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    color: #a0aec0;
    pointer-events: none;
  }
`;

const RefreshButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #1A8B8B 0%, #0D6B6B 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  margin-${props => props.$isRTL ? 'right' : 'left'}: auto;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

// View Toggle for switching between Pipeline Traces and Connection Traces
const ViewToggle = styled.div`
  display: flex;
  background: #e2e8f0;
  border-radius: 10px;
  padding: 4px;
  margin-bottom: 24px;
`;

const ViewToggleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$active ? 'white' : 'transparent'};
  color: ${props => props.$active ? '#1A8B8B' : '#64748b'};
  box-shadow: ${props => props.$active ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none'};

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    color: ${props => props.$active ? '#1A8B8B' : '#4a5568'};
  }
`;

// Connection-specific styled components
const ConnectionRow = styled(motion.div)`
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
  cursor: pointer;
  transition: background 0.2s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f7fafc;
  }
`;

const ConnectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const ConnectionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const ConnectionStatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => {
    switch (props.$status) {
      case 'connected': return '#dbeafe';
      case 'active': return '#c6f6d520';
      case 'closed': return '#f1f5f9';
      case 'error': return '#fee2e220';
      default: return '#e2e8f020';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'connected': return '#2563eb';
      case 'active': return '#22c55e';
      case 'closed': return '#64748b';
      case 'error': return '#dc2626';
      default: return '#64748b';
    }
  }};

  svg {
    width: 14px;
    height: 14px;
  }
`;

const ConnectionMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 13px;
  color: #718096;

  span {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const MessageCountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: ${props => {
    if (props.$failed > 0) return '#fee2e2';
    if (props.$success > 0) return '#dcfce7';
    return '#f1f5f9';
  }};
  color: ${props => {
    if (props.$failed > 0) return '#dc2626';
    if (props.$success > 0) return '#16a34a';
    return '#64748b';
  }};
`;

const LinkedTracesSection = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
`;

const LinkedTraceItem = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f1f5f9;
    border-color: #cbd5e0;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const LinkedTraceHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const LinkedTraceQuestion = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #2d3748;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 12px;
`;

const LifecycleTimeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`;

// Detailed Message Flow Components
const MessageFlowContainer = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 16px;
`;

const MessageFlowHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #f7fafc;
  }
`;

const MessageFlowBody = styled.div`
  padding: 16px;
`;

const FlowSection = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FlowSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: ${props => props.$bgColor || '#f8fafc'};
  border-bottom: 1px solid #e2e8f0;
  font-size: 13px;
  font-weight: 600;
  color: #4a5568;
  cursor: pointer;

  svg {
    width: 14px;
    height: 14px;
    color: ${props => props.$iconColor || '#718096'};
  }
`;

const FlowSectionBody = styled.div`
  padding: 12px 14px;
`;

// End-to-End Timeline Bar Components
const E2ETimelineContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const E2ETimelineBar = styled.div`
  display: flex;
  width: 100%;
  height: 28px;
  border-radius: 4px;
  overflow: hidden;
  background: #f1f5f9;
`;

const E2ETimelineSegment = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: ${props => props.$minWidth || '40px'};
  width: ${props => props.$width || 'auto'};
  background: ${props => props.$color || '#3b82f6'};
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 0 6px;
  position: relative;
  transition: all 0.2s;

  &:hover {
    filter: brightness(1.1);
  }

  .segment-label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const E2ETimelineLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 11px;
  color: #64748b;
`;

const E2ELegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  .legend-dot {
    width: 10px;
    height: 10px;
    border-radius: 2px;
    background: ${props => props.$color || '#3b82f6'};
  }
`;

const E2ETotalDuration = styled.div`
  display: flex;
  justify-content: flex-end;
  font-size: 13px;
  font-weight: 600;
  color: #2d3748;
  margin-top: 4px;
`;

const ApiCallItem = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }

  .api-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
  }

  .api-service {
    font-weight: 600;
    color: #2d3748;
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .api-method {
    background: #dbeafe;
    color: #2563eb;
    font-size: 10px;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 4px;
    margin-left: 8px;
  }

  .api-status {
    font-size: 12px;
    font-weight: 500;
    padding: 2px 8px;
    border-radius: 4px;
    background: ${props => props.$success ? '#dcfce7' : '#fee2e2'};
    color: ${props => props.$success ? '#16a34a' : '#dc2626'};
  }

  .api-url {
    font-size: 11px;
    color: #64748b;
    font-family: 'SF Mono', monospace;
    word-break: break-all;
  }

  .api-timing {
    font-size: 12px;
    color: #718096;
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 6px;
  }

  .api-payload {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #e2e8f0;
  }

  .payload-label {
    font-size: 10px;
    font-weight: 600;
    color: #64748b;
    margin-bottom: 4px;
  }

  .payload-content {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    padding: 8px;
    font-size: 11px;
    font-family: 'SF Mono', monospace;
    max-height: 150px;
    overflow-y: auto;
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

const StreamEventItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px dashed #e2e8f0;
  font-size: 12px;

  &:last-child {
    border-bottom: none;
  }

  .event-time {
    font-family: 'SF Mono', monospace;
    color: #64748b;
    font-size: 11px;
    min-width: 60px;
  }

  .event-type {
    background: #e0f2fe;
    color: #0369a1;
    font-size: 10px;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 4px;
    min-width: 70px;
    text-align: center;
  }

  .event-data {
    flex: 1;
    color: #4a5568;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const IntermediateResult = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 12px;
  background: ${props => {
    switch (props.$type) {
      case 'sql': return '#f0fdf4';
      case 'error': return '#fef2f2';
      default: return '#f8fafc';
    }
  }};
  border: 1px solid ${props => {
    switch (props.$type) {
      case 'sql': return '#bbf7d0';
      case 'error': return '#fecaca';
      default: return '#e2e8f0';
    }
  }};
  border-radius: 6px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }

  .result-label {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
    min-width: 100px;
  }

  .result-value {
    flex: 1;
    font-size: 13px;
    color: #2d3748;
    word-break: break-word;
    font-family: ${props => props.$mono ? "'SF Mono', monospace" : 'inherit'};
    white-space: ${props => props.$mono ? 'pre-wrap' : 'normal'};
  }
`;

const PipelineStepsInline = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #e2e8f0;
`;

const PipelineStepInline = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  margin-bottom: 4px;
  border-radius: 4px;
  background: ${props => {
    switch (props.$status) {
      case 'success': return '#f0fdf4';
      case 'error': return '#fef2f2';
      default: return '#f8fafc';
    }
  }};
  font-size: 12px;

  &:last-child {
    margin-bottom: 0;
  }

  .step-icon {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${props => {
      switch (props.$status) {
        case 'success': return '#dcfce7';
        case 'error': return '#fee2e2';
        default: return '#e2e8f0';
      }
    }};

    svg {
      width: 10px;
      height: 10px;
      color: ${props => {
        switch (props.$status) {
          case 'success': return '#16a34a';
          case 'error': return '#dc2626';
          default: return '#64748b';
        }
      }};
    }
  }

  .step-name {
    flex: 1;
    font-weight: 500;
    color: #2d3748;
  }

  .step-duration {
    color: ${props => props.$duration > 3000 ? '#f59e0b' : '#718096'};
    font-weight: ${props => props.$duration > 3000 ? '600' : '400'};
  }
`;

const LifecycleStep = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: ${props => {
    switch (props.$status) {
      case 'success': return '#f0fdf4';
      case 'error': return '#fef2f2';
      default: return '#f8fafc';
    }
  }};
  border: 1px solid ${props => {
    switch (props.$status) {
      case 'success': return '#bbf7d0';
      case 'error': return '#fecaca';
      default: return '#e2e8f0';
    }
  }};
  border-radius: 8px;

  .step-icon {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${props => {
      switch (props.$status) {
        case 'success': return '#dcfce7';
        case 'error': return '#fee2e2';
        default: return '#e2e8f0';
      }
    }};
    color: ${props => {
      switch (props.$status) {
        case 'success': return '#16a34a';
        case 'error': return '#dc2626';
        default: return '#64748b';
      }
    }};

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .step-content {
    flex: 1;
  }

  .step-name {
    font-weight: 500;
    color: #2d3748;
    font-size: 14px;
  }

  .step-meta {
    font-size: 12px;
    color: #718096;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 2px;
  }

  .step-duration {
    font-size: 13px;
    font-weight: 500;
    color: ${props => props.$duration > 1000 ? '#f59e0b' : '#22c55e'};
  }
`;

const TraceList = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  overflow: hidden;
`;

const TraceRow = styled(motion.div)`
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
  cursor: pointer;
  transition: background 0.2s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f7fafc;
  }
`;

const TraceHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const TraceQuestion = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: #2d3748;
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => {
    switch (props.$status) {
      case 'success': return '#c6f6d520';
      case 'rejected': return '#fed7d720';
      case 'unclear': return '#fef08a20';
      case 'error': return '#fee2e220';
      default: return '#e2e8f020';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'success': return '#22c55e';
      case 'rejected': return '#ef4444';
      case 'unclear': return '#eab308';
      case 'error': return '#dc2626';
      default: return '#64748b';
    }
  }};

  svg {
    width: 14px;
    height: 14px;
  }
`;

const TraceMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 13px;
  color: #718096;

  span {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #718096;

  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 16px;
    color: #cbd5e0;
  }

  h3 {
    font-size: 18px;
    color: #4a5568;
    margin: 0 0 8px 0;
  }

  p {
    font-size: 14px;
    margin: 0;
  }
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #718096;

  svg {
    width: 32px;
    height: 32px;
    color: #1A8B8B;
    margin-bottom: 16px;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

// Detail Modal
const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 800px;
  max-height: 85vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    width: 20px;
    height: 20px;
    color: #1A8B8B;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: #718096;
  border-radius: 8px;
  transition: all 0.2s;

  &:hover {
    background: #f1f5f9;
    color: #2d3748;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
`;

const DetailSection = styled.div`
  margin-bottom: 24px;

  h4 {
    font-size: 14px;
    font-weight: 600;
    color: #4a5568;
    margin: 0 0 12px 0;
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
      width: 16px;
      height: 16px;
      color: #1A8B8B;
    }
  }
`;

const DetailBox = styled.div`
  background: #f7fafc;
  border-radius: 8px;
  padding: 16px;
  font-size: 14px;
  color: #2d3748;

  pre {
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
    font-family: 'SF Mono', 'Consolas', monospace;
    font-size: 13px;
  }
`;

const StepsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// Nested children container
const ChildrenContainer = styled.div`
  margin-top: 8px;
  margin-left: 20px;
  padding-left: 16px;
  border-left: 2px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// Badge for steps that have children
const ChildrenBadge = styled.span`
  background: #e0f2fe;
  color: #0369a1;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 8px;
`;

const StepItem = styled.div`
  background: ${props => {
    if (props.$isBottleneck) return '#fef3c7';
    switch (props.$status) {
      case 'success': return '#f0fdf4';
      case 'error': return '#fef2f2';
      case 'skipped': return '#f8fafc';
      default: return '#f8fafc';
    }
  }};
  border: 1px solid ${props => {
    if (props.$isBottleneck) return '#fcd34d';
    switch (props.$status) {
      case 'success': return '#bbf7d0';
      case 'error': return '#fecaca';
      case 'skipped': return '#e2e8f0';
      default: return '#e2e8f0';
    }
  }};
  border-radius: 8px;
  padding: 12px 16px;
  ${props => props.$isBottleneck ? 'border-left: 4px solid #f59e0b;' : ''}

  .step-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  .step-name {
    font-weight: 600;
    color: #2d3748;
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
      width: 16px;
      height: 16px;
      color: ${props => props.$status === 'success' ? '#22c55e' : props.$status === 'error' ? '#ef4444' : '#718096'};
    }
  }

  .step-duration {
    font-size: 13px;
    color: ${props => props.$isBottleneck ? '#b45309' : '#718096'};
    font-weight: ${props => props.$isBottleneck ? '600' : '400'};
    display: flex;
    align-items: center;
    gap: 4px;

    svg {
      width: 14px;
      height: 14px;
    }
  }

  .bottleneck-badge {
    background: #f59e0b;
    color: white;
    font-size: 10px;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 4px;
    margin-left: 8px;
  }

  .step-details {
    font-size: 13px;
    color: #4a5568;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #e2e8f0;

    pre {
      margin: 0;
      white-space: pre-wrap;
      word-break: break-word;
      font-family: 'SF Mono', 'Consolas', monospace;
      font-size: 12px;
      background: white;
      padding: 8px;
      border-radius: 4px;
    }
  }
`;

// Timeline visualization
const TimelineBar = styled.div`
  display: flex;
  height: 24px;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 16px;
  background: #e2e8f0;
`;

const TimelineSegment = styled.div`
  height: 100%;
  min-width: 2px;
  background: ${props => {
    if (props.$isBottleneck) return '#f59e0b';
    switch (props.$status) {
      case 'success': return '#22c55e';
      case 'error': return '#ef4444';
      case 'skipped': return '#94a3b8';
      default: return '#3b82f6';
    }
  }};
  transition: all 0.3s;
  cursor: pointer;
  position: relative;

  &:hover {
    filter: brightness(1.1);
    z-index: 1;
  }

  &:hover::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background: #1e293b;
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 11px;
    white-space: nowrap;
    z-index: 10;
    margin-bottom: 4px;
  }
`;

// Step statistics summary
const StepStats = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #f8fafc;
  border-radius: 8px;
  flex-wrap: wrap;

  .stat {
    display: flex;
    flex-direction: column;
    align-items: center;

    .value {
      font-size: 18px;
      font-weight: 700;
      color: #2d3748;
    }

    .label {
      font-size: 11px;
      color: #718096;
      text-transform: uppercase;
    }

    &.warning .value {
      color: #f59e0b;
    }

    &.error .value {
      color: #ef4444;
    }
  }
`;

// Step category grouping
const StepCategory = styled.div`
  margin-bottom: 16px;

  .category-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e2e8f0;

    .category-name {
      font-size: 13px;
      font-weight: 600;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .category-duration {
      font-size: 12px;
      color: #94a3b8;
    }
  }
`;

const getStatusIcon = (status) => {
  switch (status) {
    case 'success': return <CheckCircle />;
    case 'rejected': return <XCircle />;
    case 'unclear': return <HelpCircle />;
    case 'error': return <AlertTriangle />;
    default: return <Clock />;
  }
};

const getStatusText = (status, isRTL) => {
  const texts = {
    success: isRTL ? 'ناجح' : 'Success',
    rejected: isRTL ? 'مرفوض' : 'Rejected',
    unclear: isRTL ? 'غير واضح' : 'Unclear',
    error: isRTL ? 'خطأ' : 'Error',
    processing: isRTL ? 'قيد المعالجة' : 'Processing'
  };
  return texts[status] || status;
};

const getConnectionStatusIcon = (status) => {
  switch (status) {
    case 'connected': return <Wifi />;
    case 'active': return <Activity />;
    case 'closed': return <WifiOff />;
    case 'error': return <AlertTriangle />;
    default: return <Clock />;
  }
};

const getConnectionStatusText = (status, isRTL) => {
  const texts = {
    connected: isRTL ? 'متصل' : 'Connected',
    active: isRTL ? 'نشط' : 'Active',
    closed: isRTL ? 'مغلق' : 'Closed',
    error: isRTL ? 'خطأ' : 'Error'
  };
  return texts[status] || status;
};

const getLifecycleStepIcon = (name) => {
  const icons = {
    'websocket_connect': <Wifi />,
    'welcome_sent': <MessageSquare />,
    'message_processing': <Activity />,
    'response_sent': <CheckCircle />,
    'websocket_close': <WifiOff />,
    'auth_validated': <Eye />,
    'pipeline_execution': <Zap />
  };
  return icons[name] || <Clock />;
};

function DebugDashboard({ isActive = false }) {
  const { settings } = useSettings();
  const isRTL = settings?.language === 'ar';

  // View toggle: 'pipeline' or 'connections'
  const [viewMode, setViewMode] = useState('pipeline');

  // Use ref for tracking initial load to avoid stale closure issues
  const hasLoadedRef = useRef(false);
  const prevActiveRef = useRef(isActive);

  // Pipeline traces state
  const [traces, setTraces] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTrace, setSelectedTrace] = useState(null);
  const [traceDetail, setTraceDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Connection traces state
  const [connections, setConnections] = useState([]);
  const [connectionStats, setConnectionStats] = useState(null);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [connectionDetail, setConnectionDetail] = useState(null);
  const [loadingConnectionDetail, setLoadingConnectionDetail] = useState(false);
  const [expandedMessageFlows, setExpandedMessageFlows] = useState(new Set());
  const [expandedApiCalls, setExpandedApiCalls] = useState(new Set());

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [hoursFilter, setHoursFilter] = useState(24);
  const [latencyFilter, setLatencyFilter] = useState('all'); // 'all', 'slow', 'very_slow', 'bottleneck'
  const [hideEmptyConnections, setHideEmptyConnections] = useState(true); // Hide 0-message connections by default
  const [hiddenEmptyCount, setHiddenEmptyCount] = useState(0); // Count of hidden empty connections

  // Admin user filter
  const [userFilter, setUserFilter] = useState('all');
  const [users, setUsers] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);

  // Step expansion state - collapsed by default, auto-expand errors
  const [expandedSteps, setExpandedSteps] = useState(new Set());

  const fetchTraces = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: 1,
        page_size: 50,
        hours: hoursFilter
      });
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (userFilter !== 'all' && isAdmin) {
        params.append('user_id', userFilter);
      }

      const response = await api.get(`/debug/traces?${params}`);
      setTraces(response.traces || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch traces:', err);
      setError(err.message);
    }
  }, [statusFilter, hoursFilter, userFilter, isAdmin]);

  // Fetch users list for admin filter dropdown
  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get(`/debug/users?hours=${hoursFilter}`);
      setUsers(response || []);
      setIsAdmin(true); // If we can fetch users, we're admin
    } catch (err) {
      // Not admin or no users - ignore
      setIsAdmin(false);
      setUsers([]);
    }
  }, [hoursFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get(`/debug/stats?hours=${hoursFilter}`);
      setStats(response);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [hoursFilter]);

  // Connection traces fetch functions
  const fetchConnections = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: 1,
        page_size: 50,
        hours: hoursFilter
      });
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (userFilter !== 'all' && isAdmin) {
        params.append('user_id', userFilter);
      }
      if (hideEmptyConnections) {
        params.append('min_messages', '1');
      }

      const response = await api.get(`/debug/connections?${params}`);
      setConnections(response.connections || []);
      setHiddenEmptyCount(response.hidden_empty_count || 0);
    } catch (err) {
      console.error('Failed to fetch connections:', err);
      setConnections([]);
      setHiddenEmptyCount(0);
    }
  }, [statusFilter, hoursFilter, userFilter, isAdmin, hideEmptyConnections]);

  const fetchConnectionStats = useCallback(async () => {
    try {
      const response = await api.get(`/debug/connections/stats/summary?hours=${hoursFilter}`);
      setConnectionStats(response);
    } catch (err) {
      console.error('Failed to fetch connection stats:', err);
    }
  }, [hoursFilter]);

  const loadData = useCallback(async () => {
    setLoading(true);
    if (viewMode === 'pipeline') {
      await Promise.all([fetchTraces(), fetchStats(), fetchUsers()]);
    } else {
      await Promise.all([fetchConnections(), fetchConnectionStats(), fetchUsers()]);
    }
    setLoading(false);
  }, [fetchTraces, fetchStats, fetchUsers, fetchConnections, fetchConnectionStats, viewMode]);

  const handleRefresh = async () => {
    setRefreshing(true);
    if (viewMode === 'pipeline') {
      await Promise.all([fetchTraces(), fetchStats(), fetchUsers()]);
    } else {
      await Promise.all([fetchConnections(), fetchConnectionStats(), fetchUsers()]);
    }
    setRefreshing(false);
  };

  // Open connection detail modal
  const openConnectionDetail = async (connection) => {
    setSelectedConnection(connection);
    setLoadingConnectionDetail(true);
    try {
      const response = await api.get(`/debug/connections/${connection.id}`);
      setConnectionDetail(response);
    } catch (err) {
      console.error('Failed to fetch connection detail:', err);
    }
    setLoadingConnectionDetail(false);
  };

  const closeConnectionModal = () => {
    setSelectedConnection(null);
    setConnectionDetail(null);
    setExpandedMessageFlows(new Set());
    setExpandedApiCalls(new Set());
  };

  // Toggle expanded message flow
  const toggleMessageFlow = (index) => {
    setExpandedMessageFlows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Toggle expanded API call details
  const toggleApiCall = (key) => {
    setExpandedApiCalls(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  // Open pipeline trace detail from connection (linked traces)
  const openLinkedTraceDetail = async (traceId) => {
    setLoadingDetail(true);
    try {
      const response = await api.get(`/debug/traces/${traceId}`);
      setTraceDetail(response);
      setSelectedTrace({ id: traceId }); // Set minimal trace to open modal
      closeConnectionModal(); // Close connection modal first
    } catch (err) {
      console.error('Failed to fetch trace detail:', err);
    }
    setLoadingDetail(false);
  };

  const openTraceDetail = async (trace) => {
    setSelectedTrace(trace);
    setLoadingDetail(true);
    setExpandedSteps(new Set()); // Reset expanded state
    try {
      const response = await api.get(`/debug/traces/${trace.id}`);
      setTraceDetail(response);

      // Auto-expand error steps, slow steps (>3s), and parents with children
      // Recursively find paths to auto-expand
      if (response.steps) {
        const autoExpandPaths = new Set();

        const checkStep = (step, path) => {
          const hasChildren = step.children && step.children.length > 0;
          const isError = step.status === 'error' || step.error;
          const isSlow = step.duration_ms > 3000;

          // Auto-expand if it's an error, slow, or has children
          if (isError || isSlow || hasChildren) {
            autoExpandPaths.add(path);
          }

          // Recursively check children
          if (hasChildren) {
            step.children.forEach((child, childIndex) => {
              checkStep(child, `${path}-${childIndex}`);
            });
          }
        };

        response.steps.forEach((step, index) => {
          checkStep(step, `${index}`);
        });

        setExpandedSteps(autoExpandPaths);
      }
    } catch (err) {
      console.error('Failed to fetch trace detail:', err);
    }
    setLoadingDetail(false);
  };

  // Toggle individual step expansion (supports nested paths like "0", "0-1", "0-1-2")
  const toggleStep = (stepPath) => {
    setExpandedSteps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stepPath)) {
        newSet.delete(stepPath);
      } else {
        newSet.add(stepPath);
      }
      return newSet;
    });
  };

  // Recursively collect all step paths for expand all
  const collectAllStepPaths = (steps, parentPath = '') => {
    const paths = [];
    steps.forEach((step, index) => {
      const currentPath = parentPath ? `${parentPath}-${index}` : `${index}`;
      paths.push(currentPath);
      if (step.children && step.children.length > 0) {
        paths.push(...collectAllStepPaths(step.children, currentPath));
      }
    });
    return paths;
  };

  // Expand all steps (including nested)
  const expandAllSteps = () => {
    if (traceDetail?.steps) {
      const allPaths = collectAllStepPaths(traceDetail.steps);
      setExpandedSteps(new Set(allPaths));
    }
  };

  // Collapse all steps
  const collapseAllSteps = () => {
    setExpandedSteps(new Set());
  };

  const closeModal = () => {
    setSelectedTrace(null);
    setTraceDetail(null);
  };

  // Load data when tab becomes active (detect transition from inactive to active)
  useEffect(() => {
    const wasActive = prevActiveRef.current;
    prevActiveRef.current = isActive;

    // Load if: becoming active and haven't loaded, OR already active on mount
    if (isActive && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadData();
    }
  }, [isActive, loadData]);

  // Re-fetch when view mode changes (only if already loaded once)
  useEffect(() => {
    if (hasLoadedRef.current) {
      loadData();
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch when filters change (only if already loaded once)
  useEffect(() => {
    if (hasLoadedRef.current) {
      if (viewMode === 'pipeline') {
        fetchTraces();
      } else {
        fetchConnections();
      }
    }
  }, [statusFilter, hoursFilter, userFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatDuration = (ms) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString(isRTL ? 'ar-SA' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <Container $isRTL={isRTL}>
      <Header>
        <Title>
          <Activity />
          {isRTL ? 'لوحة التتبع والتصحيح' : 'Debug Dashboard'}
        </Title>
        <Subtitle>
          {viewMode === 'pipeline'
            ? (isRTL ? 'مراقبة وتحليل استعلامات النظام' : 'Monitor and analyze pipeline traces')
            : (isRTL ? 'مراقبة دورة حياة اتصالات WebSocket' : 'Monitor WebSocket connection lifecycle')}
        </Subtitle>
      </Header>

      {/* View Toggle */}
      <ViewToggle>
        <ViewToggleButton
          $active={viewMode === 'pipeline'}
          onClick={() => {
            setViewMode('pipeline');
            setStatusFilter('all'); // Reset filter when switching views
          }}
        >
          <Zap />
          {isRTL ? 'تتبعات الأنابيب' : 'Pipeline Traces'}
        </ViewToggleButton>
        <ViewToggleButton
          $active={viewMode === 'connections'}
          onClick={() => {
            setViewMode('connections');
            setStatusFilter('all'); // Reset filter when switching views
          }}
        >
          <Wifi />
          {isRTL ? 'تتبعات الاتصال' : 'Connection Traces'}
        </ViewToggleButton>
      </ViewToggle>

      {/* Pipeline Stats Grid */}
      {viewMode === 'pipeline' && stats && (
        <StatsGrid>
          <StatCard $color="#1A8B8B" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="stat-header">
              <div className="stat-icon"><Database /></div>
            </div>
            <div className="stat-label">{isRTL ? 'إجمالي الاستعلامات' : 'Total Queries'}</div>
            <div className="stat-value">{stats.total_queries}</div>
          </StatCard>

          <StatCard $color="#22c55e" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <div className="stat-header">
              <div className="stat-icon"><CheckCircle /></div>
            </div>
            <div className="stat-label">{isRTL ? 'ناجحة' : 'Success'}</div>
            <div className="stat-value">{stats.success_count}</div>
          </StatCard>

          <StatCard $color="#ef4444" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <div className="stat-header">
              <div className="stat-icon"><XCircle /></div>
            </div>
            <div className="stat-label">{isRTL ? 'مرفوضة' : 'Rejected'}</div>
            <div className="stat-value">{stats.rejected_count}</div>
          </StatCard>

          <StatCard $color="#eab308" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <div className="stat-header">
              <div className="stat-icon"><Zap /></div>
            </div>
            <div className="stat-label">{isRTL ? 'متوسط الوقت' : 'Avg Duration'}</div>
            <div className="stat-value">{formatDuration(stats.avg_duration_ms)}</div>
          </StatCard>

          <StatCard $color="#1A8B8B" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <div className="stat-header">
              <div className="stat-icon"><BarChart2 /></div>
            </div>
            <div className="stat-label">{isRTL ? 'نسبة النجاح' : 'Success Rate'}</div>
            <div className="stat-value">{stats.success_rate}%</div>
          </StatCard>
        </StatsGrid>
      )}

      {/* Connection Stats Grid */}
      {viewMode === 'connections' && connectionStats && (
        <StatsGrid>
          <StatCard $color="#2563eb" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="stat-header">
              <div className="stat-icon"><Wifi /></div>
            </div>
            <div className="stat-label">{isRTL ? 'إجمالي الاتصالات' : 'Total Connections'}</div>
            <div className="stat-value">{connectionStats.total_connections}</div>
          </StatCard>

          <StatCard $color="#22c55e" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <div className="stat-header">
              <div className="stat-icon"><Activity /></div>
            </div>
            <div className="stat-label">{isRTL ? 'نشطة الآن' : 'Active Now'}</div>
            <div className="stat-value">{connectionStats.active_connections || 0}</div>
          </StatCard>

          <StatCard $color="#eab308" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <div className="stat-header">
              <div className="stat-icon"><MessageSquare /></div>
            </div>
            <div className="stat-label">{isRTL ? 'إجمالي الرسائل' : 'Total Messages'}</div>
            <div className="stat-value">{connectionStats.total_messages || 0}</div>
          </StatCard>

          <StatCard $color="#1A8B8B" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <div className="stat-header">
              <div className="stat-icon"><Clock /></div>
            </div>
            <div className="stat-label">{isRTL ? 'متوسط مدة الجلسة' : 'Avg Session Duration'}</div>
            <div className="stat-value">{formatDuration(connectionStats.avg_session_duration_ms)}</div>
          </StatCard>

          <StatCard $color="#ef4444" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <div className="stat-header">
              <div className="stat-icon"><XCircle /></div>
            </div>
            <div className="stat-label">{isRTL ? 'فشل الاتصالات' : 'Failed Connections'}</div>
            <div className="stat-value">{connectionStats.error_connections || 0}</div>
          </StatCard>
        </StatsGrid>
      )}

      {/* Filters */}
      <FilterBar>
        <FilterSelect $isRTL={isRTL}>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">{isRTL ? 'جميع الحالات' : 'All Status'}</option>
            {viewMode === 'pipeline' ? (
              <>
                <option value="success">{isRTL ? 'ناجح' : 'Success'}</option>
                <option value="rejected">{isRTL ? 'مرفوض' : 'Rejected'}</option>
                <option value="unclear">{isRTL ? 'غير واضح' : 'Unclear'}</option>
                <option value="error">{isRTL ? 'خطأ' : 'Error'}</option>
              </>
            ) : (
              <>
                <option value="connected">{isRTL ? 'متصل' : 'Connected'}</option>
                <option value="active">{isRTL ? 'نشط' : 'Active'}</option>
                <option value="closed">{isRTL ? 'مغلق' : 'Closed'}</option>
                <option value="error">{isRTL ? 'خطأ' : 'Error'}</option>
              </>
            )}
          </select>
          <ChevronDown />
        </FilterSelect>

        <FilterSelect $isRTL={isRTL}>
          <select value={hoursFilter} onChange={(e) => setHoursFilter(Number(e.target.value))}>
            <option value={1}>{isRTL ? 'آخر ساعة' : 'Last 1 hour'}</option>
            <option value={6}>{isRTL ? 'آخر 6 ساعات' : 'Last 6 hours'}</option>
            <option value={24}>{isRTL ? 'آخر 24 ساعة' : 'Last 24 hours'}</option>
            <option value={48}>{isRTL ? 'آخر 48 ساعة' : 'Last 48 hours'}</option>
            <option value={168}>{isRTL ? 'آخر أسبوع' : 'Last week'}</option>
          </select>
          <ChevronDown />
        </FilterSelect>

        {/* Latency filter - only for pipeline traces */}
        {viewMode === 'pipeline' && (
          <FilterSelect $isRTL={isRTL}>
            <select value={latencyFilter} onChange={(e) => setLatencyFilter(e.target.value)}>
              <option value="all">{isRTL ? 'كل السرعات' : 'All Latency'}</option>
              <option value="slow">{isRTL ? 'بطيء (>10ث)' : 'Slow (>10s)'}</option>
              <option value="very_slow">{isRTL ? 'بطيء جداً (>20ث)' : 'Very Slow (>20s)'}</option>
              <option value="bottleneck">{isRTL ? 'يحتوي اختناقات' : 'Has Bottleneck (>3s step)'}</option>
            </select>
            <ChevronDown />
          </FilterSelect>
        )}

        {/* Hide empty connections filter - only for connection traces */}
        {viewMode === 'connections' && (
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 14px',
            background: hideEmptyConnections ? '#dcfce7' : '#f1f5f9',
            borderRadius: 8,
            cursor: 'pointer',
            fontSize: 13,
            color: hideEmptyConnections ? '#16a34a' : '#64748b',
            transition: 'all 0.2s',
            border: hideEmptyConnections ? '1px solid #bbf7d0' : '1px solid #e2e8f0',
          }}>
            <input
              type="checkbox"
              checked={hideEmptyConnections}
              onChange={(e) => setHideEmptyConnections(e.target.checked)}
              style={{ accentColor: '#16a34a' }}
            />
            {isRTL ? 'إخفاء الفارغة' : 'Hide empty'}
            {hideEmptyConnections && hiddenEmptyCount > 0 && (
              <span style={{
                background: '#f1f5f9',
                padding: '2px 8px',
                borderRadius: 10,
                fontSize: 11,
                color: '#64748b'
              }}>
                {hiddenEmptyCount}
              </span>
            )}
          </label>
        )}

        {/* Admin-only: User filter dropdown */}
        {isAdmin && users.length > 0 && (
          <FilterSelect $isRTL={isRTL}>
            <select value={userFilter} onChange={(e) => setUserFilter(e.target.value)}>
              <option value="all">{isRTL ? 'جميع المستخدمين' : 'All Users'}</option>
              {users.map(user => (
                <option key={user.user_id} value={user.user_id}>
                  {user.username} ({user.trace_count})
                </option>
              ))}
            </select>
            <ChevronDown />
          </FilterSelect>
        )}

        <RefreshButton
          $isRTL={isRTL}
          onClick={handleRefresh}
          disabled={refreshing}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <RefreshCw className={refreshing ? 'spin' : ''} />
          {isRTL ? 'تحديث' : 'Refresh'}
        </RefreshButton>
      </FilterBar>

      {/* Pipeline Trace List */}
      {viewMode === 'pipeline' && (
        <TraceList>
          {loading ? (
            <LoadingState>
              <Loader />
              <span>{isRTL ? 'جاري التحميل...' : 'Loading traces...'}</span>
            </LoadingState>
          ) : error ? (
            <EmptyState>
              <AlertTriangle />
              <h3>{isRTL ? 'حدث خطأ' : 'Error Loading'}</h3>
              <p>{error}</p>
            </EmptyState>
          ) : traces.length === 0 ? (
            <EmptyState>
              <Activity />
              <h3>{isRTL ? 'لا توجد تتبعات' : 'No Traces Found'}</h3>
              <p>{isRTL ? 'لم يتم العثور على أي تتبعات للفترة المحددة' : 'No traces found for the selected time period'}</p>
            </EmptyState>
          ) : (
            traces
              .filter(trace => {
                // Apply latency filter (client-side)
                if (latencyFilter === 'all') return true;
                if (latencyFilter === 'slow') return trace.total_duration_ms > 10000;
                if (latencyFilter === 'very_slow') return trace.total_duration_ms > 20000;
                // Bottleneck filter will be applied in detail view since we don't have steps in list
                return true;
              })
              .map((trace, index) => (
              <TraceRow
                key={trace.id}
                onClick={() => openTraceDetail(trace)}
                initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <TraceHeader>
                  <TraceQuestion>
                    <StatusBadge $status={trace.status}>
                      {getStatusIcon(trace.status)}
                      {getStatusText(trace.status, isRTL)}
                    </StatusBadge>
                    {trace.question}
                  </TraceQuestion>
                  <ChevronRight style={{ color: '#a0aec0', width: 20, height: 20 }} />
                </TraceHeader>
                <TraceMeta>
                  <span><Clock /> {formatTime(trace.created_at)}</span>
                  <span><Zap /> {formatDuration(trace.total_duration_ms)}</span>
                  {trace.username && <span><Eye /> {trace.username}</span>}
                </TraceMeta>
              </TraceRow>
            ))
          )}
        </TraceList>
      )}

      {/* Connection Trace List */}
      {viewMode === 'connections' && (
        <TraceList>
          {loading ? (
            <LoadingState>
              <Loader />
              <span>{isRTL ? 'جاري التحميل...' : 'Loading connections...'}</span>
            </LoadingState>
          ) : connections.length === 0 ? (
            <EmptyState>
              <Wifi />
              <h3>{isRTL ? 'لا توجد اتصالات' : 'No Connections Found'}</h3>
              <p>{isRTL ? 'لم يتم العثور على أي اتصالات للفترة المحددة' : 'No connections found for the selected time period'}</p>
            </EmptyState>
          ) : (
            connections.map((connection, index) => (
              <ConnectionRow
                key={connection.id}
                onClick={() => openConnectionDetail(connection)}
                initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <ConnectionHeader>
                  <ConnectionInfo>
                    <ConnectionStatusBadge $status={connection.status}>
                      {getConnectionStatusIcon(connection.status)}
                      {getConnectionStatusText(connection.status, isRTL)}
                    </ConnectionStatusBadge>
                    <span style={{ fontWeight: 500, color: '#2d3748' }}>
                      {connection.connection_id?.slice(0, 8)}...
                    </span>
                    <MessageCountBadge
                      $success={connection.successful_messages || 0}
                      $failed={connection.failed_messages || 0}
                    >
                      <MessageSquare size={12} />
                      {connection.total_messages || 0} {isRTL ? 'رسائل' : 'msgs'}
                    </MessageCountBadge>
                  </ConnectionInfo>
                  <ChevronRight style={{ color: '#a0aec0', width: 20, height: 20 }} />
                </ConnectionHeader>
                <ConnectionMeta>
                  <span><Clock /> {formatTime(connection.connected_at)}</span>
                  {connection.session_duration_ms && (
                    <span><Zap /> {formatDuration(connection.session_duration_ms)}</span>
                  )}
                  {connection.username && <span><Eye /> {connection.username}</span>}
                  {connection.disconnect_reason && (
                    <span style={{ color: connection.disconnect_reason === 'error' ? '#ef4444' : '#718096' }}>
                      <WifiOff /> {connection.disconnect_reason}
                    </span>
                  )}
                </ConnectionMeta>
              </ConnectionRow>
            ))
          )}
        </TraceList>
      )}

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedTrace && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <ModalContent
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle>
                  <Activity />
                  {isRTL ? 'تفاصيل التتبع' : 'Trace Details'}
                </ModalTitle>
                <CloseButton onClick={closeModal}>
                  <X />
                </CloseButton>
              </ModalHeader>

              <ModalBody>
                {loadingDetail ? (
                  <LoadingState>
                    <Loader />
                    <span>{isRTL ? 'جاري التحميل...' : 'Loading...'}</span>
                  </LoadingState>
                ) : traceDetail ? (
                  <>
                    {/* Question */}
                    <DetailSection>
                      <h4><HelpCircle /> {isRTL ? 'السؤال' : 'Question'}</h4>
                      <DetailBox>{traceDetail.question}</DetailBox>
                    </DetailSection>

                    {/* Reformulated Question */}
                    {traceDetail.reformulated_question && traceDetail.reformulated_question !== traceDetail.question && (
                      <DetailSection>
                        <h4><RefreshCw /> {isRTL ? 'السؤال المعاد صياغته' : 'Reformulated Question'}</h4>
                        <DetailBox>{traceDetail.reformulated_question}</DetailBox>
                      </DetailSection>
                    )}

                    {/* Status & Duration */}
                    <DetailSection>
                      <h4><Activity /> {isRTL ? 'الحالة' : 'Status'}</h4>
                      <DetailBox>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                          <StatusBadge $status={traceDetail.status}>
                            {getStatusIcon(traceDetail.status)}
                            {getStatusText(traceDetail.status, isRTL)}
                          </StatusBadge>
                          <span style={{ color: '#718096' }}>
                            <Clock style={{ width: 14, height: 14, marginRight: 4 }} />
                            {formatDuration(traceDetail.total_duration_ms)}
                          </span>
                          {traceDetail.row_count !== null && (
                            <span style={{ color: '#718096' }}>
                              <Database style={{ width: 14, height: 14, marginRight: 4 }} />
                              {traceDetail.row_count} {isRTL ? 'صف' : 'rows'}
                            </span>
                          )}
                        </div>
                        {traceDetail.error_message && (
                          <div style={{ marginTop: 12, padding: 12, background: '#fef2f2', borderRadius: 8, color: '#dc2626' }}>
                            {traceDetail.error_message}
                          </div>
                        )}
                      </DetailBox>
                    </DetailSection>

                    {/* SQL Query */}
                    {traceDetail.final_sql && (
                      <DetailSection>
                        <h4><Code /> {isRTL ? 'استعلام SQL' : 'SQL Query'}</h4>
                        <DetailBox>
                          <pre>{traceDetail.final_sql}</pre>
                        </DetailBox>
                      </DetailSection>
                    )}

                    {/* Pipeline Steps */}
                    {traceDetail.steps && traceDetail.steps.length > 0 && (
                      <DetailSection>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                          <h4 style={{ margin: 0 }}><Zap /> {isRTL ? 'خطوات المعالجة' : `Pipeline Steps (${traceDetail.steps.length} steps)`}</h4>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              onClick={expandAllSteps}
                              style={{
                                padding: '6px 12px',
                                fontSize: 12,
                                background: '#f1f5f9',
                                border: '1px solid #e2e8f0',
                                borderRadius: 6,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4
                              }}
                            >
                              <ChevronDown size={14} />
                              {isRTL ? 'توسيع الكل' : 'Expand All'}
                            </button>
                            <button
                              onClick={collapseAllSteps}
                              style={{
                                padding: '6px 12px',
                                fontSize: 12,
                                background: '#f1f5f9',
                                border: '1px solid #e2e8f0',
                                borderRadius: 6,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4
                              }}
                            >
                              <ChevronRight size={14} />
                              {isRTL ? 'طي الكل' : 'Collapse All'}
                            </button>
                          </div>
                        </div>

                        {/* Step Statistics Summary */}
                        {(() => {
                          const slowSteps = traceDetail.steps.filter(s => s.duration_ms > 3000);
                          const errorSteps = traceDetail.steps.filter(s => s.status === 'error');
                          const totalStepDuration = traceDetail.steps.reduce((sum, s) => sum + (s.duration_ms || 0), 0);
                          return (
                            <StepStats>
                              <div className="stat">
                                <span className="value">{traceDetail.steps.length}</span>
                                <span className="label">{isRTL ? 'الخطوات' : 'Total Steps'}</span>
                              </div>
                              <div className={`stat ${slowSteps.length > 0 ? 'warning' : ''}`}>
                                <span className="value">{slowSteps.length}</span>
                                <span className="label">{isRTL ? 'بطيء (>3ث)' : 'Slow (>3s)'}</span>
                              </div>
                              <div className={`stat ${errorSteps.length > 0 ? 'error' : ''}`}>
                                <span className="value">{errorSteps.length}</span>
                                <span className="label">{isRTL ? 'أخطاء' : 'Errors'}</span>
                              </div>
                              <div className="stat">
                                <span className="value">{formatDuration(totalStepDuration)}</span>
                                <span className="label">{isRTL ? 'إجمالي الخطوات' : 'Steps Total'}</span>
                              </div>
                            </StepStats>
                          );
                        })()}

                        {/* Visual Timeline */}
                        <TimelineBar>
                          {traceDetail.steps.map((step, index) => {
                            const totalDuration = traceDetail.steps.reduce((sum, s) => sum + (s.duration_ms || 0), 0);
                            const widthPercent = totalDuration > 0 ? ((step.duration_ms || 0) / totalDuration) * 100 : 0;
                            const isBottleneck = step.duration_ms > 3000;
                            return (
                              <TimelineSegment
                                key={index}
                                style={{ width: `${Math.max(widthPercent, 1)}%` }}
                                $status={step.status}
                                $isBottleneck={isBottleneck}
                                data-tooltip={`${step.name}: ${formatDuration(step.duration_ms)}`}
                              />
                            );
                          })}
                        </TimelineBar>

                        <StepsList>
                          {/* Recursive step renderer for nested steps */}
                          {(function renderSteps(steps, parentPath = '') {
                            return steps.map((step, index) => {
                              const stepPath = parentPath ? `${parentPath}-${index}` : `${index}`;
                              const isBottleneck = step.duration_ms > 3000;
                              const isExpanded = expandedSteps.has(stepPath);
                              const hasChildren = step.children && step.children.length > 0;
                              const hasDetails = (step.input && Object.keys(step.input).length > 0) || step.output || step.error || hasChildren;

                              return (
                                <StepItem key={stepPath} $status={step.status} $isBottleneck={isBottleneck}>
                                  <div
                                    className="step-header"
                                    onClick={() => hasDetails && toggleStep(stepPath)}
                                    style={{ cursor: hasDetails ? 'pointer' : 'default' }}
                                  >
                                    <span className="step-name">
                                      {hasDetails && (
                                        isExpanded
                                          ? <ChevronDown size={14} style={{ color: '#718096' }} />
                                          : <ChevronRight size={14} style={{ color: '#718096' }} />
                                      )}
                                      {step.status === 'success' ? <CheckCircle /> : step.status === 'error' ? <XCircle /> : step.status === 'skipped' ? <Clock /> : <Clock />}
                                      {step.name}
                                      {hasChildren && (
                                        <ChildrenBadge>{step.children.length} {isRTL ? 'خطوات' : 'steps'}</ChildrenBadge>
                                      )}
                                      {isBottleneck && <span className="bottleneck-badge">{isRTL ? 'اختناق' : 'SLOW'}</span>}
                                    </span>
                                    <span className="step-duration">
                                      <Clock />
                                      {formatDuration(step.duration_ms)}
                                    </span>
                                  </div>
                                  {/* Collapsible Details */}
                                  {isExpanded && (
                                    <>
                                      {/* Input Data */}
                                      {step.input && Object.keys(step.input).length > 0 && (
                                        <div className="step-details">
                                          <div style={{ color: '#64748b', fontSize: 11, marginBottom: 4, fontWeight: 600 }}>INPUT:</div>
                                          <pre>{JSON.stringify(step.input, null, 2)}</pre>
                                        </div>
                                      )}
                                      {/* Output Data or Error */}
                                      {(step.output || step.error) && (
                                        <div className="step-details">
                                          {step.error ? (
                                            <>
                                              <div style={{ color: '#dc2626', fontSize: 11, marginBottom: 4, fontWeight: 600 }}>ERROR:</div>
                                              <div style={{ color: '#dc2626' }}>{step.error}</div>
                                            </>
                                          ) : (
                                            <>
                                              <div style={{ color: '#64748b', fontSize: 11, marginBottom: 4, fontWeight: 600 }}>OUTPUT:</div>
                                              <pre>{JSON.stringify(step.output, null, 2)}</pre>
                                            </>
                                          )}
                                        </div>
                                      )}
                                      {/* Nested Children */}
                                      {hasChildren && (
                                        <ChildrenContainer>
                                          {renderSteps(step.children, stepPath)}
                                        </ChildrenContainer>
                                      )}
                                    </>
                                  )}
                                </StepItem>
                              );
                            });
                          })(traceDetail.steps)}
                        </StepsList>
                      </DetailSection>
                    )}

                    {/* Final Answer */}
                    {traceDetail.final_answer && (
                      <DetailSection>
                        <h4><CheckCircle /> {isRTL ? 'الإجابة النهائية' : 'Final Answer'}</h4>
                        <DetailBox>{traceDetail.final_answer}</DetailBox>
                      </DetailSection>
                    )}
                  </>
                ) : null}
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>

      {/* Connection Detail Modal */}
      <AnimatePresence>
        {selectedConnection && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeConnectionModal}
          >
            <ModalContent
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle>
                  <Wifi />
                  {isRTL ? 'تفاصيل الاتصال' : 'Connection Details'}
                </ModalTitle>
                <CloseButton onClick={closeConnectionModal}>
                  <X />
                </CloseButton>
              </ModalHeader>

              <ModalBody>
                {loadingConnectionDetail ? (
                  <LoadingState>
                    <Loader />
                    <span>{isRTL ? 'جاري التحميل...' : 'Loading...'}</span>
                  </LoadingState>
                ) : connectionDetail ? (
                  <>
                    {/* Connection Info */}
                    <DetailSection>
                      <h4><Wifi /> {isRTL ? 'معلومات الاتصال' : 'Connection Info'}</h4>
                      <DetailBox>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                          <ConnectionStatusBadge $status={connectionDetail.status}>
                            {getConnectionStatusIcon(connectionDetail.status)}
                            {getConnectionStatusText(connectionDetail.status, isRTL)}
                          </ConnectionStatusBadge>
                          <span style={{ fontFamily: 'monospace', fontSize: 13, color: '#4a5568' }}>
                            ID: {connectionDetail.connection_id}
                          </span>
                        </div>
                        <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
                          <div>
                            <div style={{ fontSize: 11, color: '#718096', fontWeight: 600 }}>{isRTL ? 'وقت الاتصال' : 'CONNECTED AT'}</div>
                            <div style={{ fontSize: 14, color: '#2d3748' }}>{formatTime(connectionDetail.connected_at)}</div>
                          </div>
                          {connectionDetail.disconnected_at && (
                            <div>
                              <div style={{ fontSize: 11, color: '#718096', fontWeight: 600 }}>{isRTL ? 'وقت قطع الاتصال' : 'DISCONNECTED AT'}</div>
                              <div style={{ fontSize: 14, color: '#2d3748' }}>{formatTime(connectionDetail.disconnected_at)}</div>
                            </div>
                          )}
                          <div>
                            <div style={{ fontSize: 11, color: '#718096', fontWeight: 600 }}>{isRTL ? 'مدة الجلسة' : 'SESSION DURATION'}</div>
                            <div style={{ fontSize: 14, color: '#2d3748' }}>{formatDuration(connectionDetail.session_duration_ms)}</div>
                          </div>
                          {connectionDetail.disconnect_reason && (
                            <div>
                              <div style={{ fontSize: 11, color: '#718096', fontWeight: 600 }}>{isRTL ? 'سبب قطع الاتصال' : 'DISCONNECT REASON'}</div>
                              <div style={{ fontSize: 14, color: connectionDetail.disconnect_reason === 'error' ? '#ef4444' : '#2d3748' }}>
                                {connectionDetail.disconnect_reason}
                              </div>
                            </div>
                          )}
                        </div>
                      </DetailBox>
                    </DetailSection>

                    {/* Message Stats */}
                    <DetailSection>
                      <h4><MessageSquare /> {isRTL ? 'إحصائيات الرسائل' : 'Message Stats'}</h4>
                      <StepStats>
                        <div className="stat">
                          <span className="value">{connectionDetail.total_messages || 0}</span>
                          <span className="label">{isRTL ? 'إجمالي' : 'Total'}</span>
                        </div>
                        <div className="stat">
                          <span className="value">{connectionDetail.successful_messages || 0}</span>
                          <span className="label">{isRTL ? 'ناجحة' : 'Success'}</span>
                        </div>
                        <div className={`stat ${(connectionDetail.failed_messages || 0) > 0 ? 'error' : ''}`}>
                          <span className="value">{connectionDetail.failed_messages || 0}</span>
                          <span className="label">{isRTL ? 'فاشلة' : 'Failed'}</span>
                        </div>
                        <div className={`stat ${(connectionDetail.rejected_messages || 0) > 0 ? 'warning' : ''}`}>
                          <span className="value">{connectionDetail.rejected_messages || 0}</span>
                          <span className="label">{isRTL ? 'مرفوضة' : 'Rejected'}</span>
                        </div>
                        {connectionDetail.avg_message_duration_ms && (
                          <div className="stat">
                            <span className="value">{formatDuration(connectionDetail.avg_message_duration_ms)}</span>
                            <span className="label">{isRTL ? 'متوسط الوقت' : 'Avg Time'}</span>
                          </div>
                        )}
                      </StepStats>
                    </DetailSection>

                    {/* Lifecycle Timeline - Enhanced with detailed message flow */}
                    {connectionDetail.lifecycle_steps && connectionDetail.lifecycle_steps.length > 0 && (
                      <DetailSection>
                        <h4><Activity /> {isRTL ? 'خط الوقت' : 'Request Flow Timeline'}</h4>
                        <LifecycleTimeline>
                          {connectionDetail.lifecycle_steps.map((step, index) => {
                            // Check if this is a message processing step with detailed metadata
                            const isMessageProcessing = step.name === 'message_processing';
                            const hasDetailedData = isMessageProcessing && step.metadata && (
                              step.metadata.api_calls?.length > 0 ||
                              step.metadata.stream_events?.length > 0 ||
                              step.metadata.request?.full_payload ||
                              step.metadata.response
                            );
                            const isExpanded = expandedMessageFlows.has(index);

                            // Render detailed message flow for message_processing steps
                            if (isMessageProcessing && hasDetailedData) {
                              const metadata = step.metadata;
                              return (
                                <MessageFlowContainer key={index}>
                                  <MessageFlowHeader onClick={() => toggleMessageFlow(index)}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                      <div style={{
                                        width: 32, height: 32, borderRadius: '50%',
                                        background: step.status === 'success' ? '#dcfce7' : step.status === 'error' ? '#fee2e2' : '#e2e8f0',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: step.status === 'success' ? '#16a34a' : step.status === 'error' ? '#dc2626' : '#64748b'
                                      }}>
                                        <MessageSquare size={16} />
                                      </div>
                                      <div>
                                        <div style={{ fontWeight: 600, color: '#2d3748' }}>
                                          {isRTL ? 'معالجة الرسالة' : 'Message Processing'} #{metadata.request?.message_number || index + 1}
                                        </div>
                                        <div style={{ fontSize: 12, color: '#64748b' }}>
                                          {metadata.request?.question?.slice(0, 60) || 'Query'}...
                                        </div>
                                      </div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                      <StatusBadge $status={metadata.status || step.status}>
                                        {getStatusIcon(metadata.status || step.status)}
                                        {getStatusText(metadata.status || step.status, isRTL)}
                                      </StatusBadge>
                                      <span style={{ color: step.duration_ms > 5000 ? '#f59e0b' : '#22c55e', fontWeight: 500 }}>
                                        {formatDuration(metadata.duration_ms || step.duration_ms)}
                                      </span>
                                      {isExpanded ? <ChevronDown size={20} color="#718096" /> : <ChevronRight size={20} color="#718096" />}
                                    </div>
                                  </MessageFlowHeader>

                                  {isExpanded && (
                                    <MessageFlowBody>
                                      {/* Request Details */}
                                      {metadata.request && (
                                        <FlowSection>
                                          <FlowSectionHeader $bgColor="#e0f2fe" $iconColor="#0369a1">
                                            <ArrowRight size={14} />
                                            {isRTL ? 'الطلب الوارد' : 'Incoming Request'}
                                          </FlowSectionHeader>
                                          <FlowSectionBody>
                                            <IntermediateResult>
                                              <div className="result-label">{isRTL ? 'السؤال' : 'Question'}</div>
                                              <div className="result-value">{metadata.request.question}</div>
                                            </IntermediateResult>
                                            {metadata.request.full_payload && (
                                              <IntermediateResult>
                                                <div className="result-label">{isRTL ? 'البيانات' : 'Payload'}</div>
                                                <div className="result-value" style={{ fontFamily: "'SF Mono', monospace", fontSize: 11 }}>
                                                  {JSON.stringify(metadata.request.full_payload, null, 2)}
                                                </div>
                                              </IntermediateResult>
                                            )}
                                          </FlowSectionBody>
                                        </FlowSection>
                                      )}

                                      {/* End-to-End Timeline */}
                                      {metadata.request?.client_timing && (
                                        <FlowSection>
                                          <FlowSectionHeader $bgColor="#f0f9ff" $iconColor="#0284c7">
                                            <Clock size={14} />
                                            {isRTL ? 'الجدول الزمني الشامل' : 'End-to-End Timeline'}
                                          </FlowSectionHeader>
                                          <FlowSectionBody>
                                            {(() => {
                                              const clientTiming = metadata.request.client_timing;
                                              const backendDuration = metadata.duration_ms || step.duration_ms || 0;
                                              const uiPrepDuration = clientTiming.ui_prep_duration_ms || 0;

                                              // Calculate network latency (estimated as portion of total - backend)
                                              // Since we have ws_send_at and server_received_at, we can estimate
                                              const serverReceived = metadata.request.server_received_at;
                                              const wsSendAt = clientTiming.ws_send_at;
                                              let networkOutbound = 0;
                                              if (wsSendAt && serverReceived) {
                                                try {
                                                  networkOutbound = Math.max(0, new Date(serverReceived) - new Date(wsSendAt));
                                                } catch (e) {
                                                  networkOutbound = 50; // Default estimate
                                                }
                                              }

                                              // Estimate return network as similar to outbound
                                              const networkInbound = networkOutbound || 50;

                                              // Total duration
                                              const totalDuration = uiPrepDuration + networkOutbound + backendDuration + networkInbound;

                                              // Calculate widths as percentages
                                              const calcWidth = (duration) => {
                                                if (totalDuration === 0) return '25%';
                                                const pct = Math.max(5, (duration / totalDuration) * 100);
                                                return `${pct}%`;
                                              };

                                              return (
                                                <E2ETimelineContainer>
                                                  <E2ETimelineBar>
                                                    <E2ETimelineSegment
                                                      $color="#3b82f6"
                                                      $width={calcWidth(uiPrepDuration)}
                                                      title={`UI Prep: ${uiPrepDuration}ms`}
                                                    >
                                                      <span className="segment-label">{uiPrepDuration}ms</span>
                                                    </E2ETimelineSegment>
                                                    <E2ETimelineSegment
                                                      $color="#8b5cf6"
                                                      $width={calcWidth(networkOutbound)}
                                                      title={`Network →: ${networkOutbound}ms`}
                                                    >
                                                      <span className="segment-label">→</span>
                                                    </E2ETimelineSegment>
                                                    <E2ETimelineSegment
                                                      $color="#10b981"
                                                      $width={calcWidth(backendDuration)}
                                                      title={`Backend: ${backendDuration}ms`}
                                                    >
                                                      <span className="segment-label">{backendDuration}ms</span>
                                                    </E2ETimelineSegment>
                                                    <E2ETimelineSegment
                                                      $color="#8b5cf6"
                                                      $width={calcWidth(networkInbound)}
                                                      title={`Network ←: ${networkInbound}ms`}
                                                    >
                                                      <span className="segment-label">←</span>
                                                    </E2ETimelineSegment>
                                                  </E2ETimelineBar>
                                                  <E2ETimelineLegend>
                                                    <E2ELegendItem $color="#3b82f6">
                                                      <div className="legend-dot" />
                                                      <span>{isRTL ? 'تحضير UI' : 'UI Prep'} ({uiPrepDuration}ms)</span>
                                                    </E2ELegendItem>
                                                    <E2ELegendItem $color="#8b5cf6">
                                                      <div className="legend-dot" />
                                                      <span>{isRTL ? 'شبكة' : 'Network'} (~{networkOutbound + networkInbound}ms)</span>
                                                    </E2ELegendItem>
                                                    <E2ELegendItem $color="#10b981">
                                                      <div className="legend-dot" />
                                                      <span>{isRTL ? 'خادم' : 'Backend'} ({backendDuration}ms)</span>
                                                    </E2ELegendItem>
                                                  </E2ETimelineLegend>
                                                  <E2ETotalDuration>
                                                    {isRTL ? 'المجموع الكلي:' : 'Total End-to-End:'} {totalDuration}ms
                                                  </E2ETotalDuration>
                                                </E2ETimelineContainer>
                                              );
                                            })()}
                                          </FlowSectionBody>
                                        </FlowSection>
                                      )}

                                      {/* API Calls */}
                                      {metadata.api_calls && metadata.api_calls.length > 0 && (
                                        <FlowSection>
                                          <FlowSectionHeader $bgColor="#dbeafe" $iconColor="#2563eb">
                                            <Zap size={14} />
                                            {isRTL ? 'استدعاءات API' : `API Calls (${metadata.api_calls.length})`}
                                          </FlowSectionHeader>
                                          <FlowSectionBody>
                                            {metadata.api_calls.map((apiCall, apiIndex) => {
                                              const apiKey = `${index}-${apiIndex}`;
                                              const isApiExpanded = expandedApiCalls.has(apiKey);
                                              return (
                                                <ApiCallItem key={apiIndex} $success={apiCall.status_code === 200}>
                                                  <div className="api-header" onClick={() => toggleApiCall(apiKey)} style={{ cursor: 'pointer' }}>
                                                    <span className="api-service">
                                                      {isApiExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                                      {apiCall.service}
                                                      <span className="api-method">{apiCall.method}</span>
                                                    </span>
                                                    <span className="api-status">
                                                      {apiCall.status_code || (apiCall.error ? 'Error' : 'OK')}
                                                    </span>
                                                  </div>
                                                  <div className="api-url">{apiCall.url}</div>
                                                  <div className="api-timing">
                                                    <Clock size={12} />
                                                    {formatDuration(apiCall.duration_ms)}
                                                    {apiCall.error && (
                                                      <span style={{ color: '#dc2626', marginLeft: 8 }}>
                                                        {apiCall.error}
                                                      </span>
                                                    )}
                                                  </div>
                                                  {isApiExpanded && (
                                                    <div className="api-payload">
                                                      {apiCall.request && (
                                                        <>
                                                          <div className="payload-label">{isRTL ? 'الطلب' : 'REQUEST'}</div>
                                                          <div className="payload-content">
                                                            {JSON.stringify(apiCall.request, null, 2)}
                                                          </div>
                                                        </>
                                                      )}
                                                      {apiCall.response && (
                                                        <>
                                                          <div className="payload-label" style={{ marginTop: 8 }}>{isRTL ? 'الاستجابة' : 'RESPONSE'}</div>
                                                          <div className="payload-content">
                                                            {JSON.stringify(apiCall.response, null, 2)}
                                                          </div>
                                                        </>
                                                      )}
                                                    </div>
                                                  )}
                                                </ApiCallItem>
                                              );
                                            })}
                                          </FlowSectionBody>
                                        </FlowSection>
                                      )}

                                      {/* Intermediate Results */}
                                      {metadata.request?.intermediate_results && Object.keys(metadata.request.intermediate_results).length > 0 && (
                                        <FlowSection>
                                          <FlowSectionHeader $bgColor="#dcfce7" $iconColor="#16a34a">
                                            <Database size={14} />
                                            {isRTL ? 'النتائج الوسيطة' : 'Intermediate Results'}
                                          </FlowSectionHeader>
                                          <FlowSectionBody>
                                            {Object.entries(metadata.request.intermediate_results).map(([key, value]) => (
                                              <IntermediateResult
                                                key={key}
                                                $type={key.includes('sql') ? 'sql' : 'default'}
                                                $mono={key.includes('sql')}
                                              >
                                                <div className="result-label">{key.replace(/_/g, ' ')}</div>
                                                <div className="result-value">
                                                  {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                                                </div>
                                              </IntermediateResult>
                                            ))}
                                          </FlowSectionBody>
                                        </FlowSection>
                                      )}

                                      {/* Stream Events (if many, show summary) */}
                                      {metadata.stream_events && metadata.stream_events.length > 0 && (
                                        <FlowSection>
                                          <FlowSectionHeader $bgColor="#fef3c7" $iconColor="#b45309">
                                            <Activity size={14} />
                                            {isRTL ? 'أحداث البث' : `Stream Events (${metadata.stream_events.length})`}
                                          </FlowSectionHeader>
                                          <FlowSectionBody>
                                            {metadata.stream_events.slice(0, 10).map((event, eventIndex) => (
                                              <StreamEventItem key={eventIndex}>
                                                <span className="event-time">+{event.elapsed_ms}ms</span>
                                                <span className="event-type">{event.type}</span>
                                                <span className="event-data">
                                                  {event.data_preview ? (
                                                    typeof event.data_preview === 'object'
                                                      ? JSON.stringify(event.data_preview).slice(0, 60)
                                                      : String(event.data_preview).slice(0, 60)
                                                  ) : '-'}
                                                </span>
                                              </StreamEventItem>
                                            ))}
                                            {metadata.stream_events.length > 10 && (
                                              <div style={{ textAlign: 'center', fontSize: 11, color: '#64748b', marginTop: 8 }}>
                                                ... {metadata.stream_events.length - 10} more events
                                              </div>
                                            )}
                                          </FlowSectionBody>
                                        </FlowSection>
                                      )}

                                      {/* Final Response */}
                                      {metadata.response && (
                                        <FlowSection>
                                          <FlowSectionHeader $bgColor="#f0fdf4" $iconColor="#16a34a">
                                            <CheckCircle size={14} />
                                            {isRTL ? 'الاستجابة النهائية' : 'Final Response'}
                                          </FlowSectionHeader>
                                          <FlowSectionBody>
                                            {metadata.response.final_sql && (
                                              <IntermediateResult $type="sql" $mono>
                                                <div className="result-label">{isRTL ? 'SQL' : 'Generated SQL'}</div>
                                                <div className="result-value">{metadata.response.final_sql}</div>
                                              </IntermediateResult>
                                            )}
                                            {metadata.response.final_answer && (
                                              <IntermediateResult>
                                                <div className="result-label">{isRTL ? 'الإجابة' : 'Answer'}</div>
                                                <div className="result-value">{metadata.response.final_answer}</div>
                                              </IntermediateResult>
                                            )}
                                          </FlowSectionBody>
                                        </FlowSection>
                                      )}

                                      {/* Link to full pipeline trace */}
                                      {metadata.trace_id && (
                                        <div
                                          onClick={() => openLinkedTraceDetail(metadata.trace_id)}
                                          style={{
                                            marginTop: 12, padding: '10px 14px', background: '#f1f5f9',
                                            borderRadius: 8, cursor: 'pointer', display: 'flex',
                                            alignItems: 'center', justifyContent: 'space-between',
                                            transition: 'background 0.2s'
                                          }}
                                          onMouseOver={(e) => e.currentTarget.style.background = '#e2e8f0'}
                                          onMouseOut={(e) => e.currentTarget.style.background = '#f1f5f9'}
                                        >
                                          <span style={{ fontSize: 13, color: '#4a5568', display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <Eye size={14} />
                                            {isRTL ? 'عرض تفاصيل الأنبوب الكاملة' : 'View Full Pipeline Trace Details'}
                                          </span>
                                          <ArrowRight size={16} style={{ color: '#1A8B8B' }} />
                                        </div>
                                      )}
                                    </MessageFlowBody>
                                  )}
                                </MessageFlowContainer>
                              );
                            }

                            // Render simple lifecycle step (websocket_connect, welcome_sent, etc.)
                            return (
                              <LifecycleStep
                                key={index}
                                $status={step.status}
                                $duration={step.duration_ms}
                              >
                                <div className="step-icon">
                                  {getLifecycleStepIcon(step.name)}
                                </div>
                                <div className="step-content">
                                  <div className="step-name">{step.name.replace(/_/g, ' ')}</div>
                                  <div className="step-meta">
                                    {step.start_time && <span>{formatTime(step.start_time)}</span>}
                                    {step.metadata?.question && (
                                      <span style={{ color: '#4a5568', fontStyle: 'italic' }}>
                                        "{step.metadata.question.slice(0, 50)}..."
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="step-duration">
                                  {formatDuration(step.duration_ms)}
                                </div>
                              </LifecycleStep>
                            );
                          })}
                        </LifecycleTimeline>
                      </DetailSection>
                    )}

                    {/* Linked Pipeline Traces */}
                    {connectionDetail.linked_traces && connectionDetail.linked_traces.length > 0 && (
                      <LinkedTracesSection>
                        <h4 style={{ fontSize: 14, fontWeight: 600, color: '#4a5568', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Link2 size={16} style={{ color: '#1A8B8B' }} />
                          {isRTL ? 'الاستعلامات المرتبطة' : `Linked Queries (${connectionDetail.linked_traces.length})`}
                        </h4>
                        {connectionDetail.linked_traces.map((trace, index) => (
                          <LinkedTraceItem
                            key={trace.id || index}
                            onClick={() => openLinkedTraceDetail(trace.id)}
                          >
                            <LinkedTraceHeader>
                              <LinkedTraceQuestion>{trace.question}</LinkedTraceQuestion>
                              <StatusBadge $status={trace.status}>
                                {getStatusIcon(trace.status)}
                                {getStatusText(trace.status, isRTL)}
                              </StatusBadge>
                            </LinkedTraceHeader>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: '#718096' }}>
                              <span><Clock size={12} /> {formatTime(trace.created_at)}</span>
                              <span><Zap size={12} /> {formatDuration(trace.total_duration_ms)}</span>
                              <ArrowRight size={14} style={{ marginLeft: 'auto', color: '#1A8B8B' }} />
                            </div>
                          </LinkedTraceItem>
                        ))}
                      </LinkedTracesSection>
                    )}

                    {/* Client Info */}
                    {connectionDetail.client_info && Object.keys(connectionDetail.client_info).length > 0 && (
                      <DetailSection>
                        <h4><Eye /> {isRTL ? 'معلومات العميل' : 'Client Info'}</h4>
                        <DetailBox>
                          <pre>{JSON.stringify(connectionDetail.client_info, null, 2)}</pre>
                        </DetailBox>
                      </DetailSection>
                    )}
                  </>
                ) : null}
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </Container>
  );
}

export default DebugDashboard;
